import React from 'react';
import { Button } from '../../ui';
import { MEDIA_PAGE_SIZES, MediaPageSize } from './mediaLibraryQuery';

type PaginationItem = number | 'start-ellipsis' | 'end-ellipsis';

interface MediaPaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  loading: boolean;
  pageSize?: MediaPageSize;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: MediaPageSize) => void;
}

/**
 * Builds a compact, stable pagination range. Small libraries expose every page;
 * larger ones keep the first/last page plus a three-page window around the
 * current page.
 */
export const buildPaginationItems = (
  currentPage: number,
  totalPages: number
): PaginationItem[] => {
  const total = Math.max(1, Math.floor(totalPages));
  const current = Math.min(total, Math.max(1, Math.floor(currentPage)));

  if (total <= 7) return Array.from({ length: total }, (_, index) => index + 1);
  if (current <= 4) return [1, 2, 3, 4, 5, 'end-ellipsis', total];
  if (current >= total - 3) {
    return [1, 'start-ellipsis', total - 4, total - 3, total - 2, total - 1, total];
  }
  return [
    1,
    'start-ellipsis',
    current - 1,
    current,
    current + 1,
    'end-ellipsis',
    total,
  ];
};

const MediaPagination: React.FC<MediaPaginationProps> = ({
  currentPage,
  totalPages,
  totalItems,
  loading,
  pageSize = 20,
  onPageChange,
  onPageSizeChange = () => {},
}) => {
  const total = Math.max(1, Math.floor(totalPages));
  const current = Math.min(total, Math.max(1, Math.floor(currentPage)));
  const pageItems = React.useMemo(
    () => buildPaginationItems(current, total),
    [current, total]
  );

  if (totalItems <= 0) return null;

  const changePage = (page: number) => {
    const target = Math.min(total, Math.max(1, Math.floor(page)));
    if (!loading && target !== current) onPageChange(target);
  };

  return (
    <nav
      aria-label="Media library pagination"
      className="flex flex-col gap-2 pt-2 border-t border-white/10 sm:flex-row sm:items-center sm:justify-between"
    >
      <span className="text-[11px] text-zinc-400" aria-live="polite">
        Page {current} of {total} · {totalItems} total
      </span>

      <div className="flex flex-wrap items-center gap-1">
        <label className="mr-2 flex items-center gap-1 text-[11px] text-zinc-400">
          <span>Per page</span>
          <select
            className="select select-sm"
            aria-label="Items per page"
            value={pageSize}
            disabled={loading}
            onChange={(event) => onPageSizeChange(Number(event.target.value) as MediaPageSize)}
          >
            {MEDIA_PAGE_SIZES.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>

        {total > 1 && (
          <>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Previous page"
              onClick={() => changePage(current - 1)}
              disabled={current <= 1 || loading}
            >
              ← Prev
            </Button>

            <div className="flex items-center gap-1" aria-label="Page numbers">
              {pageItems.map((item) => {
                if (typeof item !== 'number') {
                  return (
                    <span
                      key={item}
                      aria-hidden="true"
                      className="inline-flex min-w-8 items-center justify-center text-xs text-zinc-500"
                    >
                      …
                    </span>
                  );
                }

                const isCurrent = item === current;
                return (
                  <Button
                    key={item}
                    type="button"
                    variant={isCurrent ? 'primary' : 'ghost'}
                    size="sm"
                    className="min-w-8 px-2"
                    aria-label={isCurrent ? `Page ${item}, current page` : `Page ${item}`}
                    aria-current={isCurrent ? 'page' : undefined}
                    onClick={() => changePage(item)}
                    disabled={loading}
                  >
                    {item}
                  </Button>
                );
              })}
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Next page"
              onClick={() => changePage(current + 1)}
              disabled={current >= total || loading}
            >
              Next →
            </Button>

            {total > 7 && (
              <label className="ml-1 flex items-center gap-1 text-[11px] text-zinc-400">
                <span>Go to</span>
                <select
                  className="select select-sm"
                  aria-label="Go to page"
                  value={current}
                  disabled={loading}
                  onChange={(event) => changePage(Number(event.target.value))}
                >
                  {Array.from({ length: total }, (_, index) => index + 1).map((page) => (
                    <option key={page} value={page}>
                      Page {page}
                    </option>
                  ))}
                </select>
              </label>
            )}
          </>
        )}
      </div>
    </nav>
  );
};

export default MediaPagination;
